/* eslint-disable */
/**
 * Trial-Expired page.
 *
 * Reached when:
 *   - /api/auth/me returns access_status === "trial_expired"
 *   - any product API call returns 402 with reason "trial_expired"
 *
 * Three actions:
 *   1. Upgrade Now      → /pricing (or whatever billing route the user has)
 *   2. Contact Support  → mailto: link
 *   3. Log out
 */
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Lock, Mail, LogOut, ArrowRight } from "lucide-react";

export default function TrialExpiredPage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  return (
    <div
      className="min-h-screen bg-[#070A12] text-slate-200 flex items-center justify-center px-6"
      data-testid="trial-expired-page"
    >
      <div className="w-full max-w-lg rounded-2xl border border-slate-800/70 bg-[#0B1120] p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
            <Lock className="w-4 h-4 text-amber-400" />
          </div>
          <span
            className="text-[11px] uppercase tracking-[0.2em] text-amber-300"
            data-testid="trial-expired-eyebrow"
          >
            Access locked
          </span>
        </div>

        <h1
          className="text-3xl font-semibold text-slate-50 leading-tight"
          data-testid="trial-expired-title"
        >
          Your free trial has ended
        </h1>
        <p
          className="mt-2 text-sm text-slate-400 leading-relaxed"
          data-testid="trial-expired-copy"
        >
          To continue using Sentient Vault, please upgrade your account.
          Your data remains safe and untouched — you can resume the moment
          your subscription is active.
        </p>

        {user?.email && (
          <p className="mt-4 text-[11px] text-slate-500 font-mono">
            Signed in as <span className="text-slate-300">{user.email}</span>
          </p>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
            onClick={() => navigate("/pricing")}
            data-testid="trial-expired-upgrade-btn"
          >
            Upgrade Now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800/60"
            onClick={() => {
              window.location.href =
                "mailto:support@sentient-vault.com?subject=Trial%20Expired%20-%20Upgrade%20Help";
            }}
            data-testid="trial-expired-support-btn"
          >
            <Mail className="w-4 h-4 mr-1" /> Contact Support
          </Button>
        </div>

        <button
          type="button"
          onClick={() => {
            logout();
            navigate("/login");
          }}
          className="mt-6 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 transition"
          data-testid="trial-expired-logout-btn"
        >
          <LogOut className="w-3 h-3" /> Log out
        </button>
      </div>
    </div>
  );
}
